import * as React from "react";
import { useMappedState } from "redux-react-hook";
import * as moment from "moment-timezone";
import * as _ from "lodash";

import {
  getDeliveryCutoff,
  getDeliveryWeekLeadTimes
} from "src/core/drops/utils/inherited-settings";
import {
  deliveryDateHasPassed,
  isFinalStatus,
  orderIsLockedByStatus
} from "src/core/orders/utils/orders";
import { useDrop } from "src/core/drops/hooks";
import type { IStore } from "src/core/shared/store";
import { OrderPaymentStatus } from "src/core/orders/api/orders.api";
import useGlobalSettings from "src/core/shared/hooks/useGlobalSettings";
import { useDaysToCutoff } from "src/core/orders/hooks/validation";
import { useDropShutdowns } from "src/modules/shutdowns/hooks/useDropShutdowns";

import useCurrentUser from "../../../users/hooks/useCurrentUser";
import {
  IOrder,
  OrderOrigin
} from "../../api/orders.api";
import useOrderIsBeforeCutoff from "./useOrderIsBeforeCutoff";

/**
 * Check if an order is before cutoff
 *
 * @param {IOrder} order
 * @returns {(string | null)}
 */
const useCanAmendOrder = () => {
  const orderIsBeforeCutOff = useOrderIsBeforeCutoff();
  const daysToCutoff = useDaysToCutoff();
  const [ { drop } ] = useDrop();
  const [ { currentRole } ] = useCurrentUser();
  const { checkShutdownDate } = useDropShutdowns();

  const { rjlDeliveryNumbers, displaySettings } = useMappedState(({ global }: IStore) => ({
    rjlDeliveryNumbers: global.settings?.rjlDeliveryNumbers,
    displaySettings: global.settings?.displaySettings
  }));

  const [ { globalSettings } ] = useGlobalSettings();

  const canAmendOrder = React.useCallback(
    (order: IOrder) => {
      if (!drop) {
        return `No ${globalSettings?.terminology.drop.singular.text}`;
      } else {
        const leadTimes = getDeliveryWeekLeadTimes(drop.groupedSettings);
        const defaultCutoff = getDeliveryCutoff(drop.groupedSettings);

        if (order.orderStatus && isFinalStatus(order.orderStatus)) {
          return "Order cannot be amended, as it is in final status";
        }

        if (!(order.origin === OrderOrigin.Prediction && currentRole?.type === "admin") && order.orderStatus && orderIsLockedByStatus(order.orderStatus)) {
          return `Cannot edit orders in a status of ${order.orderStatus.name}`;
        }

        if (!leadTimes || !defaultCutoff) {
          return "Order cannot be submitted.";
        }

        // Only do delivery date validation if it's a date-led order
        if (order.orderDeliveryDate) {
          // if the date matches a shutdown dont allow creation
          const hasShutdownDate = checkShutdownDate(order.orderDeliveryDate, order.orderVariantId);

          if (hasShutdownDate) {
            return `Order amendments on this day have been blocked due to - ${hasShutdownDate.shutdown.reason}`;
          }

          // check the rjl delivery number validates correctly
          if (rjlDeliveryNumbers && order.rjlDeliveryNumber) {
            const orderDeliveryNumber = _.find(rjlDeliveryNumbers, dn => dn.number === order.rjlDeliveryNumber);

            // if theres a min lead time then validate the cutoff of the order on this not the order cutoff
            if (orderDeliveryNumber?.options?.minLeadTimeGroup) {
              const minLeadTimeGroup = _.find(globalSettings?.leadTimeGroups, ltg => ltg.name === orderDeliveryNumber?.options?.minLeadTimeGroup);

              // shouldnt ever happen return a string to block editing though
              if (!minLeadTimeGroup) {
                return "Could not find matching lead time group";
              }
              const minLeadTimeCutoff = daysToCutoff(order.orderDeliveryDate, order.orderVariantId, minLeadTimeGroup);

              if (minLeadTimeCutoff === null || minLeadTimeCutoff < 0) {
                return displaySettings?.order?.cutoffMessage || "Order cannot be submitted, as cutoff has passed";
              }
            }
          }

          // if the delivery date is in the past then - FAIL
          if (deliveryDateHasPassed(order.orderDeliveryDate)) {
            return "Order cannot be submitted, as delivery date is in the past";
          }

          // if the deliverydate is before cutoff then - FAIL
          if (orderIsBeforeCutOff(order, "longest")) {
            if (order.amendmentExpiryDate && moment.utc().isAfter(order.amendmentExpiryDate) && moment.utc(order.amendmentExpiryDate).isAfter(moment.utc())) {
              return "Order cannot be amended, as the amendment expiry time has passed";
            }

            return displaySettings?.order?.cutoffMessage || "Order cannot be submitted, as cutoff has passed";
          }
        }

        if (order.paymentStatus === OrderPaymentStatus.CAPTURED || order.paymentStatus === OrderPaymentStatus.UNCAPTURED) {
          return "An order cannot be changed after payment has been taken";
        }

        if (order.paymentStatus === OrderPaymentStatus.FAILED) {
          return "Payment failed, the order cannot be changed while the payment is failed";
        }

        return null;
      }
    },
    [
      drop,
      globalSettings?.terminology.drop.singular.text,
      globalSettings?.leadTimeGroups,
      currentRole?.type,
      checkShutdownDate,
      rjlDeliveryNumbers,
      orderIsBeforeCutOff,
      daysToCutoff,
      displaySettings?.order?.cutoffMessage
    ]
  );

  return canAmendOrder;
};

export default useCanAmendOrder;
